import React from 'react';
import { AlertCircle, CheckCircle, ShieldCheck, Sparkles, Inbox, Clock } from 'lucide-react';
import type { VerificationStatus } from '../types/verifyEmail';

interface VerifyEmailLeftPanelProps {
  status: VerificationStatus;
}

export const VerifyEmailLeftPanel: React.FC<VerifyEmailLeftPanelProps> = ({ status }) => {
  if (status === 'success') {
    return (
      <>
        <div className="space-y-4">
          <h1
            className="text-4xl font-bold leading-tight"
            style={{ color: 'var(--color-text-primary)' }}
          >
            You're all set!
          </h1>
          <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
            Your account is now fully activated. Get ready to supercharge your development workflow
            with AI-powered insights.
          </p>
        </div>

        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-accent-teal)' }}
            >
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Account secured
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Your email is verified and your account is protected
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-accent-blue)' }}
            >
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Full access unlocked
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                All AI-powered features are now available to you
              </p>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="space-y-4">
        <h1
          className="text-4xl font-bold leading-tight"
          style={{ color: 'var(--color-text-primary)' }}
        >
          {status === 'awaiting' ? 'Almost there!' : 'Email Verification'}
        </h1>
        <p className="text-lg leading-relaxed" style={{ color: 'var(--color-text-secondary)' }}>
          {status === 'awaiting'
            ? "We've sent a verification link to your email. Click the link to activate your account and unlock all the features of Autar."
            : "We're verifying your email address to secure your account and unlock all Autar features."}
        </p>
      </div>

      {status === 'awaiting' && (
        <div className="space-y-4">
          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-accent-teal)' }}
            >
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Open your email
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                Look for an email from Autar in your inbox
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-accent-blue)' }}
            >
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Click the verification link
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                The link will verify your email address
              </p>
            </div>
          </div>

          <div className="flex items-start gap-4">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0"
              style={{ backgroundColor: 'var(--color-accent-green)' }}
            >
              <ShieldCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                Start using Autar
              </h3>
              <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                You'll be automatically signed in and ready to go
              </p>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {status === 'awaiting' && (
          <>
            <div
              className="p-4 rounded-xl flex items-start gap-3"
              style={{
                backgroundColor: 'rgba(59, 130, 246, 0.1)',
                border: '1px solid rgba(59, 130, 246, 0.2)',
              }}
            >
              <Clock
                className="w-5 h-5 shrink-0 mt-0.5"
                style={{ color: 'var(--color-accent-blue)' }}
              />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Link expires in 24 hours
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  For security, verification links are time-limited
                </p>
              </div>
            </div>

            <div
              className="p-4 rounded-xl flex items-start gap-3"
              style={{
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.2)',
              }}
            >
              <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" style={{ color: '#f59e0b' }} />
              <div>
                <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                  Check your spam folder
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  Sometimes emails end up there by mistake
                </p>
              </div>
            </div>
          </>
        )}

        {status !== 'awaiting' && (
          <div
            className="p-4 rounded-xl flex items-start gap-3"
            style={{
              backgroundColor: 'rgba(20, 184, 166, 0.1)',
              border: '1px solid rgba(20, 184, 166, 0.2)',
            }}
          >
            <Sparkles
              className="w-5 h-5 shrink-0 mt-0.5"
              style={{ color: 'var(--color-accent-teal)' }}
            />
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                Secure verification
              </p>
              <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                Your email verification is processed securely
              </p>
            </div>
          </div>
        )}
      </div>
    </>
  );
};
